import { useEffect, useRef, useState } from 'react';

import type { DealsFieldOption } from '../../hooks/useDealsFieldOptions';
import styles from './DealsPage.module.css';

/**
 * Phase 2 — ENUM dropdown for the Deals card field grid.
 *
 * Spec: docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §7.
 *
 * Two modes:
 *  - 'single' — native <select> wrapped to match the project's input
 *    styling. Operates on a string value. Empty string = no selection.
 *  - 'multi'  — chip list with a +add picker. Operates on a string[].
 *    Adding picks from a popover listbox; removing clicks ✕ on a chip.
 *
 * Inactive (soft-deleted) options ship from the backend with
 * `active=false`. They render in single-mode as ⌀ greyed-out + sorted
 * last so operators can still re-pick a legacy value but visually see
 * it's no longer recommended. Multi-mode hides inactives from the +add
 * picker (operators can't ADD an inactive value to a new edit) but
 * still renders existing chips that point at inactive values.
 *
 * Descriptions render as native title-tooltips on focus/hover — the
 * Phase 1 AnnotationTooltip handles richer per-row metadata; this is
 * just the option-level tooltip.
 */

interface FieldDropdownProps {
  /** Field name (used only for ARIA labels). */
  fieldName: string;
  /** Current value. String for single-mode, string[] for multi-mode. */
  value: string | string[] | null;
  options: DealsFieldOption[];
  mode: 'single' | 'multi';
  /** Called with the new value when the user picks/clears. */
  onChange: (next: string | string[]) => void;
  /** Whether the input is mid-edit (saving). */
  disabled?: boolean;
  /** Optional placeholder shown in single mode when value is empty. */
  placeholder?: string;
}

export function FieldDropdown({
  fieldName,
  value,
  options,
  mode,
  onChange,
  disabled,
  placeholder,
}: FieldDropdownProps) {
  if (mode === 'single') {
    return (
      <SingleSelect
        fieldName={fieldName}
        value={(value as string | null) ?? ''}
        options={options}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
      />
    );
  }
  return (
    <MultiSelect
      fieldName={fieldName}
      value={Array.isArray(value) ? value : value ? [String(value)] : []}
      options={options}
      onChange={onChange}
      disabled={disabled}
    />
  );
}


function SingleSelect({
  fieldName,
  value,
  options,
  onChange,
  disabled,
  placeholder,
}: {
  fieldName: string;
  value: string;
  options: DealsFieldOption[];
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  // If the current value isn't in the option set (e.g. legacy value
  // that's been removed from CV), render it as a virtual inactive
  // option at the top so the user can see what's currently selected.
  const inOptions = options.some((o) => o.value === value);
  const effectiveOptions: DealsFieldOption[] =
    value && !inOptions
      ? [{ value, label: value, description: null, active: false }, ...options]
      : options;

  // Find the description for the currently-selected value, if any.
  const selected = effectiveOptions.find((o) => o.value === value);

  return (
    <select
      aria-label={fieldName}
      className={styles.fieldDropdownSelect}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      title={selected?.description ?? undefined}
    >
      {!value && (
        <option value="">{placeholder ?? '— pick —'}</option>
      )}
      {effectiveOptions.map((opt) => (
        <option
          key={opt.value}
          value={opt.value}
          title={opt.description ?? undefined}
        >
          {opt.label}
          {!opt.active ? ' (inactive)' : ''}
        </option>
      ))}
    </select>
  );
}


function MultiSelect({
  fieldName,
  value,
  options,
  onChange,
  disabled,
}: {
  fieldName: string;
  value: string[];
  options: DealsFieldOption[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  const selectedSet = new Set(value);
  // Picker only offers active options NOT already chosen. Inactive
  // values stay rendered as chips if they were previously selected
  // but can't be ADDED to a fresh selection.
  const addable = options.filter(
    (opt) => opt.active && !selectedSet.has(opt.value),
  );

  function addValue(v: string) {
    onChange([...value, v]);
    setPickerOpen(false);
  }

  function removeValue(v: string) {
    onChange(value.filter((x) => x !== v));
  }

  return (
    <div
      ref={wrapperRef}
      className={styles.fieldDropdownMulti}
      aria-label={fieldName}
    >
      {value.map((v) => {
        const opt = options.find((o) => o.value === v);
        const isInactive = !!opt && !opt.active;
        return (
          <span
            key={v}
            className={`${styles.fieldDropdownChip} ${isInactive ? styles.fieldDropdownChipInactive : ''}`}
            title={opt?.description ?? undefined}
          >
            <span>{opt?.label ?? v}</span>
            {!disabled && (
              <button
                type="button"
                className={styles.fieldDropdownChipRemove}
                onClick={() => removeValue(v)}
                aria-label={`Remove ${opt?.label ?? v}`}
              >
                ×
              </button>
            )}
          </span>
        );
      })}
      {!disabled && addable.length > 0 && (
        <button
          type="button"
          className={styles.fieldDropdownAddBtn}
          onClick={() => setPickerOpen((p) => !p)}
          aria-expanded={pickerOpen}
        >
          + add…
        </button>
      )}
      {pickerOpen && (
        <div className={styles.fieldDropdownPicker} role="listbox">
          {addable.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={styles.fieldDropdownPickerOption}
              onClick={() => addValue(opt.value)}
              title={opt.description ?? undefined}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
